import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import KeywordPicker from './KeywordPicker';
import useFilterValues from '../../../../hooks/useFilterValues';
import useAcceptableTagValues from '../../../../hooks/useAcceptableTagValues';
import FilterPlateFormContext from '../../../../contexts/FilterPlateFormContext';
import useFilterInput from '../../../../hooks/useFilterInput';
import useDateScope from '../../../../hooks/useDateScope';
import filterTypeCheckers from '../../filterTypeCheckers';
import useContextualDataTypes from '../../hooks/useContextualDataTypes';
import getIdentifier from '../../../../getIdentifier';
import { DefaultDatasetFiltersContext } from '../../../../contextProviders/SplashScreenProviders/DatasetProviders/DefaultDatasetFiltersProvider';
import mergeFilterInputs from '../../../../mergeFilterInputs';
import FilterPlatesContext from '../../../../contexts/FilterPlatesContext';

const ALL_TIME_DATE_SCOPE: DateRangeInput = {};

const useEditingTextFilter = () => {
  const { drillDown } = useContext(FilterPlateFormContext);

  if (!drillDown) {
    return undefined;
  }
  if (filterTypeCheckers.isTextFilter(drillDown)) {
    return drillDown;
  }

  return undefined;
};

const useFilterInputForKeywordPicker = ({
  field,
  dataTypes,
  searchText,
}: {
  field: string | undefined;
  dataTypes: string[];
  searchText: string;
}) => {
  const { isDefaultFiltersDisabled, isValueLookupUnfiltered } =
    useContext(FilterPlatesContext);
  const { defaultDatasetFiltersLookup } = useContext(
    DefaultDatasetFiltersContext,
  );
  const wildcardFilterForText = useMemo((): FilterInput | undefined => {
    if (!field || searchText === '') {
      return undefined;
    }
    const wildcard: WildcardFilterInput = {
      pattern: `*${searchText}*`,
      field,
    };

    return {
      wildcardFilters: [wildcard],
    };
  }, [field, searchText]);
  const { filterInput: baseFilterInput } = useFilterInput({
    field,
    dataTypes,
  });
  const filterInput = useMemo(() => {
    if (isValueLookupUnfiltered) {
      const datasetOnly: FilterInput = { dataType: dataTypes };
      return wildcardFilterForText
        ? mergeFilterInputs(wildcardFilterForText, datasetOnly)
        : datasetOnly;
    }

    const baseFilterWithWildcardForText = wildcardFilterForText
      ? mergeFilterInputs(wildcardFilterForText, baseFilterInput)
      : baseFilterInput;

    if (dataTypes.length !== 1) {
      return baseFilterWithWildcardForText;
    }

    if (isDefaultFiltersDisabled) {
      return baseFilterWithWildcardForText;
    }

    // If there is just one dataType, apply the DSF for it, if one exists
    const dsf = defaultDatasetFiltersLookup[dataTypes[0]];
    if (!dsf) {
      return baseFilterWithWildcardForText;
    }

    return mergeFilterInputs(dsf.filterInput, baseFilterWithWildcardForText);
  }, [
    baseFilterInput,
    dataTypes,
    defaultDatasetFiltersLookup,
    isDefaultFiltersDisabled,
    isValueLookupUnfiltered,
    wildcardFilterForText,
  ]);

  return filterInput;
};

const KeywordPickerContainer = ({
  mode,
  editingId,
}: {
  mode: 'is one of' | 'is not one of';
  editingId?: string;
}) => {
  const { field, isEditing, onDrillDownConfirmed, dataset } = useContext(
    FilterPlateFormContext,
  );
  const dataTypes = useContextualDataTypes();
  const editingTextFilter = useEditingTextFilter();
  const [searchText, setSearchText] = useState('');

  const filterInput = useFilterInputForKeywordPicker({
    field,
    dataTypes,
    searchText,
  });
  const { isValueLookupUnfiltered } = useContext(FilterPlatesContext);
  const contextualDateScope = useDateScope({});
  const dateScope = isValueLookupUnfiltered
    ? ALL_TIME_DATE_SCOPE
    : contextualDateScope;

  const [alreadySelectedKeywords] = useState<string[]>(
    editingTextFilter ? editingTextFilter.keywordValues || [] : [],
  );
  const [keywordValues, setKeywordValues] = useState<string[]>(
    editingTextFilter ? editingTextFilter.keywordValues || [] : [],
  );
  const [blanks, setBlanks] = useState<boolean | undefined>(
    editingTextFilter ? editingTextFilter.keywordsExists : undefined,
  );
  const { values, isLoading: isLoadingDataTypes } = useFilterValues({
    field,
    dateScope,
    filterInput,
  });

  // Surface SE-defined tags for this dataset even when no record carries them
  // yet, so a metric can be built against a tag before it is applied to data.
  const acceptableTagValues = useAcceptableTagValues({
    dataTypes: field === 'tags' ? dataTypes : [],
  });

  const mergedValues = useMemo(() => {
    if (field !== 'tags' || acceptableTagValues.length === 0) {
      return values;
    }
    return Array.from(new Set([...values, ...acceptableTagValues]));
  }, [field, values, acceptableTagValues]);

  const [optionsWithoutPreSelected, setOptionsWithoutPreSelected] = useState<
    string[]
  >([]);

  useEffect(() => {
    const newOptionsWithoutPreSelected = alreadySelectedKeywords
      ? mergedValues.filter((o) => !alreadySelectedKeywords.includes(o))
      : mergedValues;
    setOptionsWithoutPreSelected(newOptionsWithoutPreSelected);
  }, [alreadySelectedKeywords, mergedValues]);

  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  useEffect(() => {
    const newFilteredOptions = (() => {
      if (searchText === '') {
        return optionsWithoutPreSelected;
      }

      return optionsWithoutPreSelected.filter((o) =>
        o.toLowerCase().includes(searchText.toLowerCase()),
      );
    })();
    setFilteredOptions(newFilteredOptions);
  }, [optionsWithoutPreSelected, searchText]);

  const onSearchTextChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const onKeywordSelected = (keyword: string) => {
    setKeywordValues([...(keywordValues || []), keyword]);
  };

  const onKeywordDeselected = (keyword: string) => {
    setKeywordValues([...(keywordValues || []).filter((k) => k !== keyword)]);
  };

  const clearSelectedOptions = () => {
    setKeywordValues([]);
  };

  const onBlanksToggled = () => {
    setBlanks((e) => {
      if (e === undefined) {
        return false;
      } else {
        return !e;
      }
    });
  };

  const onConfirm = useCallback(() => {
    if (!field) {
      return;
    }
    const newDrillDown: TextFilter = {
      id: editingId ? editingId : getIdentifier(undefined, true),
      field,
      fieldType: 'text' as 'text',
      keywordValues,
      isExclude: mode === 'is not one of',
      dataset,
      mode: 'editing' as 'editing',
      keywordsExists: blanks,
    };

    onDrillDownConfirmed(newDrillDown);
  }, [
    dataset,
    editingId,
    blanks,
    field,
    mode,
    keywordValues,
    onDrillDownConfirmed,
  ]);

  const isSearching = searchText !== '';
  const selectedOptions = keywordValues || [];
  const canClearSelectedOptions =
    !isSearching &&
    alreadySelectedKeywords &&
    selectedOptions.filter((o) => alreadySelectedKeywords.includes(o)).length >
      1;

  const isValid = keywordValues.length > 0 || blanks !== undefined;
  const showBlanksTicked = blanks === undefined ? false : !blanks;

  return (
    <KeywordPicker
      isSearching={isSearching}
      canClearSelectedOptions={!!canClearSelectedOptions}
      alreadySelectedKeywords={
        alreadySelectedKeywords
          ? alreadySelectedKeywords.filter((k) =>
              k.toLowerCase().includes(searchText.toLowerCase()),
            )
          : []
      }
      options={filteredOptions}
      selectedOptions={selectedOptions}
      searchText={searchText}
      onSearchTextChanged={onSearchTextChanged}
      onKeywordSelected={onKeywordSelected}
      onKeywordDeselected={onKeywordDeselected}
      onConfirm={onConfirm}
      isEditing={isEditing}
      isLoadingDataTypes={isLoadingDataTypes}
      clearSelectedOptions={clearSelectedOptions}
      onBlanksToggled={onBlanksToggled}
      showBlanksTicked={showBlanksTicked}
      isValid={isValid}
    />
  );
};

export default KeywordPickerContainer;
